import { useTranslation } from "react-i18next";
import { formatHolidayWindow, HOLIDAY_WINDOWS_MAX } from "@ckgo2/shared";

export interface HolidayWindowValue {
  from: string;
  to: string;
}

interface Props {
  value: HolidayWindowValue[];
  onChange: (windows: HolidayWindowValue[]) => void;
}

/**
 * Editor termínových oken svátku — opakovaný řádek dvou datumových polí.
 *
 * Okna NEJSOU opis vyhlášky: jsou to dny, které se návštěvníkovi po kliknutí na tlačítko
 * předvyplní do filtru termínu. Proto se smějí (a mají) posouvat mimo samotný svátek —
 * svátek v pondělí znamená, že lidé odjíždějí už v pátek. Popisek pod řádkem ukazuje
 * DOSLOVA to, co uvidí návštěvník na tlačítku.
 */
export default function HolidayWindowsEditor({ value, onChange }: Props) {
  const { t } = useTranslation();
  const windows = value || [];

  const addWindow = () => onChange([...windows, { from: "", to: "" }]);
  const removeWindow = (index: number) => onChange(windows.filter((_, i) => i !== index));
  const updateWindow = (index: number, patch: Partial<HolidayWindowValue>) => {
    const updated = [...windows];
    updated[index] = { ...updated[index], ...patch };
    // Konec nesmí předběhnout začátek — táž ochrana jako v datumovém poli na webu,
    // jinak by schéma odmítlo uložení až po odeslání formuláře.
    if (patch.from && updated[index].to && updated[index].to < patch.from) {
      updated[index].to = patch.from;
    }
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {windows.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">
          {t("content.holidays.windows.empty")}
        </p>
      )}

      {windows.map((window, i) => (
        <div
          key={i}
          className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-36 flex-1">
              <label className="label">{t("content.holidays.windows.from")}</label>
              <input
                type="date"
                value={window.from || ""}
                onChange={(e) => updateWindow(i, { from: e.target.value })}
                className="input"
              />
            </div>
            <div className="min-w-36 flex-1">
              <label className="label">{t("content.holidays.windows.to")}</label>
              <input
                type="date"
                value={window.to || ""}
                min={window.from || undefined}
                onChange={(e) => updateWindow(i, { to: e.target.value })}
                className="input"
              />
            </div>
            <button
              type="button"
              onClick={() => removeWindow(i)}
              className="pb-2 text-sm text-red-500 hover:text-red-700"
            >
              {t("common.actions.remove")}
            </button>
          </div>
          {window.from && window.to && window.to >= window.from && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("content.holidays.windows.preview", {
                label: formatHolidayWindow({ from: window.from, to: window.to }),
              })}
            </p>
          )}
          {/* Rozepsaný řádek se od uložení NEZAHAZUJE (přišlo by se o zadané datum),
              takže na něj musí upozornit editor — jinak by operátor narazil až na
              chybu ze schématu po odeslání celého formuláře. */}
          {!window.from !== !window.to && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              {t("content.holidays.windows.incomplete")}
            </p>
          )}
        </div>
      ))}

      {/* Strop drží schéma (`HOLIDAY_WINDOWS_MAX`); tlačítko se skryje, aby operátor
          nenarazil až na 400 po odeslání celého formuláře. */}
      {windows.length < HOLIDAY_WINDOWS_MAX && (
        <button type="button" onClick={addWindow} className="btn-secondary text-sm">
          {t("content.holidays.windows.add")}
        </button>
      )}
    </div>
  );
}
